import styled, { css } from 'styled-components';
import type { Column } from '../../Table';
import { Ellipsis } from '../../../utility/Ellipsis';
import { memo } from 'react';

type TableRowProps<T> = {
  columns: Column<T>[];
  rowData: T;
  rowColor?: string;
  onRowClick?: () => void;
  onExpandRow?: () => void;
  isExpanded?: boolean;
  roundedRows?: boolean;
  hideSeparator?: boolean;
  insetRowHover?: boolean;
};

export const TableRowImpl = <T,>({
  columns,
  rowData,
  rowColor,
  onRowClick,
  onExpandRow,
  isExpanded,
  roundedRows,
  hideSeparator,
  insetRowHover,
}: TableRowProps<T>) => {
  return (
    <>
      <RowContainer
        insetRowHover={insetRowHover}
        onClick={() => {
          onRowClick?.();
          onExpandRow?.();
        }}
      >
        {columns.map((column: Column<T>, index: number) => {
          const cellContent = column.valueSelector(rowData);

          return (
            <Cell
              isCellClickable={(!column.columnClickDisabled && !!onRowClick) || !!onExpandRow}
              key={index}
              alignment={column.alignment}
              rowColor={rowColor}
              isExpanded={isExpanded}
              roundedRows={roundedRows}
              hideSeparator={hideSeparator}
            >
              {typeof cellContent === 'string' ? <Ellipsis>{cellContent}</Ellipsis> : cellContent}
            </Cell>
          );
        })}
      </RowContainer>
    </>
  );
};

export const TableRow = memo(TableRowImpl) as typeof TableRowImpl;

export const RowContainer = styled.tr.withConfig({
  shouldForwardProp: (prop) => prop !== 'insetRowHover',
})<{ insetRowHover?: boolean }>`
  display: contents;

  &:hover {
    > td {
      background-color: ${({ insetRowHover, theme }) =>
        insetRowHover ? 'transparent' : theme.colors.prototype.secondary.replace(')', '/50%)')};
    }

    > td::before {
      opacity: ${({ insetRowHover }) => (insetRowHover ? 1 : 0)};
    }
  }

  @media (hover: none) and (pointer: coarse) {
    &:hover {
      > td {
        background-color: unset;
      }

      > td::before {
        opacity: 0;
      }
    }
  }
`;

const cellBorderRadiusStyle = css`
  &:first-child {
    border-top-left-radius: 12px;
    border-bottom-left-radius: 12px;
  }

  &:last-child {
    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
  }
`;

export const Cell = styled.td.withConfig({
  shouldForwardProp: (prop) =>
    prop !== 'alignment' &&
    prop !== 'rowColor' &&
    prop !== 'isCellClickable' &&
    prop !== 'isExpanded' &&
    prop !== 'roundedRows' &&
    prop !== 'hideSeparator',
})<{
  alignment?: 'left' | 'center' | 'right';
  rowColor?: string;
  isCellClickable?: boolean;
  isExpanded?: boolean;
  roundedRows?: boolean;
  hideSeparator?: boolean;
}>`
  position: relative;
  min-height: 36px;
  font-size: ${({ theme }) => theme.fontSizes.normal};
  font-weight: 400;
  padding: 4px 8px;
  display: flex;
  justify-content: ${({ alignment }) =>
    alignment === 'center' ? 'center' : alignment === 'right' ? 'flex-end' : 'flex-start'};
  align-items: center;
  color: ${({ theme }) => theme.colors.prototype.mutedForeground};
  background-color: ${({ isExpanded, rowColor, theme }) =>
    isExpanded ? theme.colors.prototype.secondary.replace(')', '/50%)') : rowColor};
  transition: background-color 200ms;
  cursor: ${({ isCellClickable }) => isCellClickable && 'pointer'};
  border-bottom: ${({ hideSeparator, theme }) => !hideSeparator && `solid 1px ${theme.colors.prototype.border}`};

  &::before {
    content: '';
    position: absolute;
    inset: 5px 0;
    background-color: ${({ theme }) => theme.colors.prototype.secondary};
    opacity: 0;
    transition: opacity 200ms;
    pointer-events: none;
  }

  > * {
    position: relative;
    z-index: 1;
  }

  ${({ roundedRows }) => roundedRows && cellBorderRadiusStyle}

  ${({ roundedRows }) =>
    roundedRows &&
    css`
      &:first-child::before {
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
      }

      &:last-child::before {
        border-top-right-radius: 12px;
        border-bottom-right-radius: 12px;
      }
    `}
`;
